import {
  DeleteOutlined,
  DownloadOutlined,
  ReloadOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Divider,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  TimePicker,
  Upload,
  message,
} from 'antd';
import type { UploadFile } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatTenantDateTime } from '../lib/tenant';

type BackupStatus = 'QUEUED' | 'RUNNING' | 'SUCCEEDED' | 'FAILED';
type BackupType = 'DATABASE' | 'ATTACHMENTS';
type BackupFormat = 'custom' | 'sql' | 'tar.gz';
type BackupSource = 'MANUAL' | 'SCHEDULED';
type BackupJob = {
  id: string;
  status: BackupStatus;
  backupType: BackupType;
  format: BackupFormat;
  source: BackupSource;
  fileName: string;
  sizeBytes: number | null;
  sha256: string | null;
  errorMessage: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  operatorName: string | null;
};
type BackupScheduleSettings = {
  enabled: boolean;
  frequency: 'DAILY' | 'WEEKLY';
  backupScope: BackupType | 'BOTH';
  timeOfDay: string;
  weekday: number;
  retentionCount: number;
  retentionDays: number;
};
type RestoreStatus = 'UPLOADED' | BackupStatus;
type RestoreJob = {
  id: string;
  status: RestoreStatus;
  format: BackupFormat;
  fileName: string;
  sizeBytes: number | null;
  sha256: string;
  errorMessage: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  operatorName: string | null;
};

function formatBytes(value: number | null): string {
  if (value === null) return '-';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  if (value < 1024 * 1024 * 1024)
    return `${(value / 1024 / 1024).toFixed(1)} MB`;
  return `${(value / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function formatLabelKey(format: BackupFormat): string {
  return format === 'tar.gz' ? 'tarGz' : format;
}

export function BackupPage() {
  const { t } = useTranslation();
  const [backups, setBackups] = useState<BackupJob[]>([]);
  const [restores, setRestores] = useState<RestoreJob[]>([]);
  const [scheduleSettings, setScheduleSettings] =
    useState<BackupScheduleSettings>();
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [format, setFormat] = useState<BackupFormat>('custom');
  const [backupType, setBackupType] = useState<BackupType>('DATABASE');
  const [restoreFile, setRestoreFile] = useState<UploadFile>();
  const [savingSettings, setSavingSettings] = useState(false);
  const [messageApi, holder] = message.useMessage();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [backupResponse, restoreResponse] = await Promise.all([
        fetch('/api/v1/backups', { credentials: 'include' }),
        fetch('/api/v1/backups/restores', { credentials: 'include' }),
      ]);
      const settingsResponse = await fetch('/api/v1/backups/settings', {
        credentials: 'include',
      });
      if (
        backupResponse.status === 401 ||
        restoreResponse.status === 401 ||
        settingsResponse.status === 401
      ) {
        window.location.assign('/login');
        return;
      }
      if (!backupResponse.ok || !restoreResponse.ok || !settingsResponse.ok)
        throw new Error();
      const backupPayload = (await backupResponse.json()) as {
        items: BackupJob[];
      };
      const restorePayload = (await restoreResponse.json()) as {
        items: RestoreJob[];
      };
      const settingsPayload =
        (await settingsResponse.json()) as BackupScheduleSettings;
      setBackups(backupPayload.items);
      setRestores(restorePayload.items);
      setScheduleSettings(settingsPayload);
    } catch {
      await messageApi.error(t('backup.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [messageApi, t]);

  const saveScheduleSettings = async () => {
    if (!scheduleSettings) return;
    setSavingSettings(true);
    try {
      const response = await fetch('/api/v1/backups/settings', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(scheduleSettings),
      });
      if (!response.ok) throw new Error();
      setScheduleSettings((await response.json()) as BackupScheduleSettings);
      await messageApi.success(t('backup.scheduleSaved'));
    } catch {
      await messageApi.error(t('backup.scheduleSaveFailed'));
    } finally {
      setSavingSettings(false);
    }
  };

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (
      !backups.some((backup) =>
        ['QUEUED', 'RUNNING'].includes(backup.status),
      ) &&
      !restores.some((restore) =>
        ['QUEUED', 'RUNNING'].includes(restore.status),
      )
    )
      return undefined;
    const timer = window.setInterval(() => void load(), 3000);
    return () => window.clearInterval(timer);
  }, [backups, restores, load]);

  const createBackup = async () => {
    setCreating(true);
    try {
      const response = await fetch('/api/v1/backups', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          backupType,
          format: backupType === 'ATTACHMENTS' ? 'tar.gz' : format,
        }),
      });
      if (!response.ok) throw new Error();
      const created = (await response.json()) as BackupJob;
      setBackups((current) => [created, ...current]);
      await messageApi.success(t('backup.created'));
    } catch {
      await messageApi.error(t('backup.createFailed'));
    } finally {
      setCreating(false);
    }
  };

  const uploadRestore = async () => {
    const file = restoreFile?.originFileObj;
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch('/api/v1/backups/restores', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (!response.ok) throw new Error();
      setRestoreFile(undefined);
      await messageApi.success(t('backup.restoreUploaded'));
      await load();
    } catch {
      await messageApi.error(t('backup.restoreUploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const startRestore = async (id: string) => {
    try {
      const response = await fetch(`/api/v1/backups/restores/${id}/start`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) throw new Error();
      await messageApi.success(t('backup.restoreQueued'));
      await load();
    } catch {
      await messageApi.error(t('backup.restoreStartFailed'));
    }
  };

  const deleteBackup = async (id: string) => {
    try {
      const response = await fetch(`/api/v1/backups/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) throw new Error();
      setBackups((current) => current.filter((backup) => backup.id !== id));
      await messageApi.success(t('backup.deleted'));
    } catch {
      await messageApi.error(t('backup.deleteFailed'));
    }
  };

  const statusTag = (status: BackupStatus | 'UPLOADED') => {
    const color =
      status === 'SUCCEEDED'
        ? 'success'
        : status === 'FAILED'
          ? 'error'
          : status === 'RUNNING'
            ? 'processing'
            : status === 'UPLOADED'
              ? 'warning'
              : 'default';
    return <Tag color={color}>{t(`backup.status.${status}`)}</Tag>;
  };

  const schedulePanel = scheduleSettings ? (
    <section className="backup-section">
      <div className="backup-section-title">{t('backup.scheduleTitle')}</div>
      <Space wrap size="middle" align="end">
        <Space direction="vertical" size={4}>
          <span>{t('backup.scheduleEnabled')}</span>
          <Switch
            checked={scheduleSettings.enabled}
            onChange={(enabled) =>
              setScheduleSettings((current) =>
                current ? { ...current, enabled } : current,
              )
            }
          />
        </Space>
        <Space direction="vertical" size={4}>
          <span>{t('backup.scheduleScope')}</span>
          <Select
            value={scheduleSettings.backupScope}
            disabled={!scheduleSettings.enabled}
            style={{ width: 150 }}
            options={[
              { label: t('backup.backupTypes.DATABASE'), value: 'DATABASE' },
              {
                label: t('backup.backupTypes.ATTACHMENTS'),
                value: 'ATTACHMENTS',
              },
              { label: t('backup.backupTypes.BOTH'), value: 'BOTH' },
            ]}
            onChange={(backupScope: BackupScheduleSettings['backupScope']) =>
              setScheduleSettings((current) =>
                current ? { ...current, backupScope } : current,
              )
            }
          />
        </Space>
        <Space direction="vertical" size={4}>
          <span>{t('backup.scheduleFrequency')}</span>
          <Select
            value={scheduleSettings.frequency}
            disabled={!scheduleSettings.enabled}
            style={{ width: 120 }}
            options={[
              { label: t('backup.frequencies.daily'), value: 'DAILY' },
              { label: t('backup.frequencies.weekly'), value: 'WEEKLY' },
            ]}
            onChange={(frequency: BackupScheduleSettings['frequency']) =>
              setScheduleSettings((current) =>
                current ? { ...current, frequency } : current,
              )
            }
          />
        </Space>
        {scheduleSettings.frequency === 'WEEKLY' && (
          <Space direction="vertical" size={4}>
            <span>{t('backup.scheduleWeekday')}</span>
            <Select
              value={scheduleSettings.weekday}
              disabled={!scheduleSettings.enabled}
              style={{ width: 120 }}
              options={[0, 1, 2, 3, 4, 5, 6].map((value) => ({
                label: t(`backup.weekdays.${value}`),
                value,
              }))}
              onChange={(weekday: number) =>
                setScheduleSettings((current) =>
                  current ? { ...current, weekday } : current,
                )
              }
            />
          </Space>
        )}
        <Space direction="vertical" size={4}>
          <span>{t('backup.scheduleTime')}</span>
          <TimePicker
            format="HH:mm"
            value={dayjs(scheduleSettings.timeOfDay, 'HH:mm')}
            disabled={!scheduleSettings.enabled}
            needConfirm={false}
            onChange={(value: Dayjs | null) =>
              setScheduleSettings((current) =>
                current
                  ? { ...current, timeOfDay: value?.format('HH:mm') ?? '02:00' }
                  : current,
              )
            }
          />
        </Space>
        <Space direction="vertical" size={4}>
          <span>{t('backup.retentionCount')}</span>
          <InputNumber
            min={1}
            max={365}
            value={scheduleSettings.retentionCount}
            onChange={(retentionCount) =>
              setScheduleSettings((current) =>
                current && retentionCount !== null
                  ? { ...current, retentionCount }
                  : current,
              )
            }
          />
        </Space>
        <Space direction="vertical" size={4}>
          <span>{t('backup.retentionDays')}</span>
          <InputNumber
            min={1}
            max={3650}
            value={scheduleSettings.retentionDays}
            onChange={(retentionDays) =>
              setScheduleSettings((current) =>
                current && retentionDays !== null
                  ? { ...current, retentionDays }
                  : current,
              )
            }
          />
        </Space>
        <Button
          type="primary"
          loading={savingSettings}
          onClick={() => void saveScheduleSettings()}
        >
          {t('common.save')}
        </Button>
      </Space>
      <div className="backup-settings-hint">{t('backup.scheduleHint')}</div>
    </section>
  ) : null;

  return (
    <section>
      {holder}
      <Card title={t('backup.title')}>
        {schedulePanel}
        {schedulePanel && <Divider />}
        <section className="backup-section">
          <div className="backup-section-title">{t('backup.manualTitle')}</div>
          <Space wrap size="middle" align="end">
            <Space direction="vertical" size={4}>
              <span>{t('backup.backupType')}</span>
              <Select
                aria-label={t('backup.backupType')}
                value={backupType}
                style={{ width: 160 }}
                options={[
                  {
                    label: t('backup.backupTypes.DATABASE'),
                    value: 'DATABASE',
                  },
                  {
                    label: t('backup.backupTypes.ATTACHMENTS'),
                    value: 'ATTACHMENTS',
                  },
                ]}
                onChange={(value: BackupType) => setBackupType(value)}
              />
            </Space>
            {backupType === 'DATABASE' ? (
              <Space direction="vertical" size={4}>
                <span>{t('backup.format')}</span>
                <Select
                  aria-label={t('backup.format')}
                  value={format}
                  style={{ width: 160 }}
                  options={[
                    { label: t('backup.formats.custom'), value: 'custom' },
                    { label: t('backup.formats.sql'), value: 'sql' },
                  ]}
                  onChange={(value: BackupFormat) => setFormat(value)}
                />
              </Space>
            ) : (
              <Space direction="vertical" size={4}>
                <span>{t('backup.format')}</span>
                <Tag color="cyan">{t('backup.formats.tarGz')}</Tag>
              </Space>
            )}
            <Button
              type="primary"
              loading={creating}
              onClick={() => void createBackup()}
            >
              {t('backup.create')}
            </Button>
          </Space>
          <div className="backup-settings-hint">{t('backup.manualHint')}</div>
        </section>
        <Divider />
        <section className="backup-section">
          <div className="backup-list-toolbar">
            <div className="backup-section-title">{t('backup.listTitle')}</div>
            <Button
              type="text"
              shape="circle"
              aria-label={t('backup.refresh')}
              icon={<ReloadOutlined />}
              loading={loading}
              onClick={() => void load()}
            />
          </div>
          <Table
            size="small"
            rowKey="id"
            loading={loading}
            dataSource={backups}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: t('backup.empty') }}
            columns={[
              {
                title: t('backup.backupType'),
                dataIndex: 'backupType',
                render: (value: BackupType) => (
                  <Tag color={value === 'DATABASE' ? 'blue' : 'cyan'}>
                    {t(`backup.backupTypes.${value}`)}
                  </Tag>
                ),
              },
              {
                title: t('backup.format'),
                dataIndex: 'format',
                render: (value: BackupFormat) =>
                  t(`backup.formats.${formatLabelKey(value)}`),
              },
              {
                title: t('backup.source'),
                dataIndex: 'source',
                render: (value: BackupSource) => t(`backup.sources.${value}`),
              },
              {
                title: t('backup.statusLabel'),
                dataIndex: 'status',
                render: (value: BackupStatus, row: BackupJob) => (
                  <Space direction="vertical" size={0}>
                    {statusTag(value)}
                    {row.errorMessage && (
                      <span
                        className="backup-error-message"
                        title={row.errorMessage}
                      >
                        {row.errorMessage}
                      </span>
                    )}
                  </Space>
                ),
              },
              {
                title: t('backup.fileName'),
                dataIndex: 'fileName',
              },
              {
                title: t('backup.size'),
                dataIndex: 'sizeBytes',
                render: (value: number | null) => formatBytes(value),
              },
              {
                title: t('backup.createdAt'),
                dataIndex: 'createdAt',
                render: (value: string) => formatTenantDateTime(value),
              },
              {
                title: t('backup.completedAt'),
                dataIndex: 'completedAt',
                render: (value: string | null) => formatTenantDateTime(value),
              },
              {
                title: t('backup.operator'),
                dataIndex: 'operatorName',
                render: (value: string | null) => value ?? '-',
              },
              {
                title: t('backup.checksum'),
                dataIndex: 'sha256',
                render: (value: string | null) =>
                  value ? (
                    <code title={value}>{value.slice(0, 12)}…</code>
                  ) : (
                    '-'
                  ),
              },
              {
                title: t('common.actions'),
                render: (_: unknown, row: BackupJob) => (
                  <Space>
                    <Button
                      type="link"
                      icon={<DownloadOutlined />}
                      disabled={row.status !== 'SUCCEEDED'}
                      onClick={() => {
                        window.location.href = `/api/v1/backups/${row.id}/download`;
                      }}
                    >
                      {t('backup.download')}
                    </Button>
                    <Popconfirm
                      title={t('backup.confirmDelete')}
                      description={t('backup.confirmDeleteDescription')}
                      onConfirm={() => void deleteBackup(row.id)}
                      okButtonProps={{ danger: true }}
                    >
                      <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        disabled={
                          row.status === 'QUEUED' || row.status === 'RUNNING'
                        }
                      >
                        {t('backup.delete')}
                      </Button>
                    </Popconfirm>
                  </Space>
                ),
              },
            ]}
          />
        </section>
      </Card>
      <Card title={t('backup.restoreTitle')} style={{ marginTop: 16 }}>
        <Alert
          type="warning"
          showIcon
          message={t('backup.restoreWarning')}
          description={t('backup.restoreDescription')}
          style={{ marginBottom: 16 }}
        />
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Upload
            accept=".dump,.backup,.sql"
            maxCount={1}
            beforeUpload={() => false}
            fileList={restoreFile ? [restoreFile] : []}
            onChange={({ fileList }) => setRestoreFile(fileList[0])}
            onRemove={() => {
              setRestoreFile(undefined);
              return true;
            }}
          >
            <Button icon={<UploadOutlined />}>
              {t('backup.chooseRestoreFile')}
            </Button>
          </Upload>
          <Button
            type="primary"
            disabled={!restoreFile?.originFileObj}
            loading={uploading}
            onClick={() => void uploadRestore()}
          >
            {t('backup.uploadRestore')}
          </Button>
        </Space>
        <Table
          size="small"
          rowKey="id"
          loading={loading}
          dataSource={restores}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: t('backup.restoreEmpty') }}
          style={{ marginTop: 20 }}
          columns={[
            {
              title: t('backup.statusLabel'),
              dataIndex: 'status',
              render: (value: RestoreStatus, row: RestoreJob) => (
                <Space direction="vertical" size={0}>
                  {statusTag(value)}
                  {row.errorMessage && (
                    <span
                      className="backup-error-message"
                      title={row.errorMessage}
                    >
                      {row.errorMessage}
                    </span>
                  )}
                </Space>
              ),
            },
            {
              title: t('backup.format'),
              dataIndex: 'format',
              render: (value: BackupFormat) =>
                t(`backup.formats.${formatLabelKey(value)}`),
            },
            { title: t('backup.fileName'), dataIndex: 'fileName' },
            {
              title: t('backup.size'),
              dataIndex: 'sizeBytes',
              render: (value: number | null) => formatBytes(value),
            },
            {
              title: t('backup.createdAt'),
              dataIndex: 'createdAt',
              render: (value: string) => formatTenantDateTime(value),
            },
            {
              title: t('backup.completedAt'),
              dataIndex: 'completedAt',
              render: (value: string | null) => formatTenantDateTime(value),
            },
            {
              title: t('backup.operator'),
              dataIndex: 'operatorName',
              render: (value: string | null) => value ?? '-',
            },
            {
              title: t('common.actions'),
              render: (_: unknown, row: RestoreJob) =>
                row.status === 'UPLOADED' ? (
                  <Popconfirm
                    title={t('backup.confirmRestore')}
                    description={t('backup.confirmRestoreDescription')}
                    onConfirm={() => void startRestore(row.id)}
                    okButtonProps={{ danger: true }}
                  >
                    <Button type="link" danger>
                      {t('backup.startRestore')}
                    </Button>
                  </Popconfirm>
                ) : null,
            },
          ]}
        />
      </Card>
    </section>
  );
}
